import { Directive, HostListener, Input, inject } from '@angular/core';
import { PermissionService } from './permission.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { Action } from './permission.model';

/**
 * Button-level permission guard (Tier 2 of the permission UX pattern).
 *
 * Tier 3 of the permission UX pattern:
 *   Page blocked   → UnauthorizedComponent (full-page, via guard)
 *   Button blocked → THIS directive (toast on click)
 *   Partial blocked → PermissionBlockedComponent (inline message)
 *
 * When the user clicks a button that they don't have permission for,
 * the click is cancelled and a toast is shown instead of executing the action.
 *
 * Usage:
 *   <button [clbRequires]="'create'" (click)="onCreate()">Add</button>
 *
 *   With a custom message:
 *   <button [clbRequires]="'delete'"
 *           clbRequiresMessage="Only admins can delete records."
 *           (click)="onDelete()">Delete</button>
 */
@Directive({
  selector: '[clbRequires]',
  standalone: false,
})
export class PermissionButtonDirective {
  private permissionService = inject(PermissionService);
  private toast = inject(ToastService);

  @Input() clbRequires!: Action;
  @Input() clbRequiresMessage?: string;

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.permissionService.can(this.clbRequires)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.toast.showWarning(
        this.clbRequiresMessage ?? "You don't have permission to perform this action."
      );
    }
  }
}
